import React, { useState, useRef, useEffect, useMemo } from 'react';
import { User } from '../types';
import { AtSign } from 'lucide-react';
import { stripMentionMarkup, parseStoredMentions, buildStoredText } from '../utils/mentions';

interface MentionInputProps {
  value: string; // Valor no formato @[Nome](user:UUID)
  onChange: (val: string) => void;
  users: User[];
  placeholder?: string;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  users,
  placeholder,
  className,
  onKeyDown,
  disabled
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Estado interno para o texto humano e as menções conhecidas
  const [displayValue, setDisplayValue] = useState(stripMentionMarkup(value));
  const [knownMentions, setKnownMentions] = useState<{id: string, name: string}[]>(parseStoredMentions(value));

  // Sincronizar quando o valor externo mudar (ex: ao limpar o campo após salvar)
  useEffect(() => {
    const stripped = stripMentionMarkup(value);
    if (stripped !== displayValue) {
      setDisplayValue(stripped);
      setKnownMentions(parseStoredMentions(value));
    }
  }, [value]);

  const filteredUsers = useMemo(() => {
    if (!filterText) return users.slice(0, 5);
    return users
      .filter(u => u.name.toLowerCase().includes(filterText.toLowerCase()))
      .slice(0, 5);
  }, [users, filterText]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const pos = e.target.selectionStart;
    setCursorPos(pos);
    setDisplayValue(newText);
    
    // Notificar pai imediatamente com o texto processado
    onChange(buildStoredText(newText, knownMentions));

    // Lógica do @
    const textBeforeCursor = newText.substring(0, pos);
    const lastAtIdx = textBeforeCursor.lastIndexOf('@');

    if (lastAtIdx !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIdx + 1);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setFilterText(textAfterAt);
        setShowSuggestions(true);
        setSelectedIndex(0);
        return;
      }
    }
    setShowSuggestions(false);
  };

  const selectUser = (user: User) => {
    const lastAtIdx = displayValue.lastIndexOf('@', cursorPos - 1);
    const textBeforeAt = displayValue.substring(0, lastAtIdx);
    const textAfterCursor = displayValue.substring(cursorPos);
    
    const newName = user.name;
    const newText = textBeforeAt + `@${newName} ` + textAfterCursor;
    
    const newMentions = [...knownMentions];
    if (!newMentions.find(m => m.id === user.id)) {
      newMentions.push({ id: user.id, name: user.name });
    }

    setDisplayValue(newText);
    setKnownMentions(newMentions);
    setShowSuggestions(false);
    
    // Atualizar o pai com o markup construído
    onChange(buildStoredText(newText, newMentions));

    setTimeout(() => {
      textareaRef.current?.focus();
      const newPos = lastAtIdx + newName.length + 2;
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 10);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredUsers.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (filteredUsers.length > 0) {
          e.preventDefault();
          selectUser(filteredUsers[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    } else if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div className="relative w-full">
      <textarea
        ref={textareaRef}
        value={displayValue}
        onChange={handleTextChange}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        rows={2}
      />
      
      {showSuggestions && filteredUsers.length > 0 && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-2xl shadow-2xl z-[160] overflow-hidden animate-in slide-in-from-bottom-2">
          <div className="p-3 border-b border-neutral-100 dark:border-navy-800 bg-neutral-50 dark:bg-navy-950 flex items-center gap-2">
            <AtSign size={12} className="text-parliament-600" />
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mencionar Equipe</span>
          </div>
          <div className="p-1">
            {filteredUsers.map((user, idx) => (
              <button
                key={user.id}
                type="button"
                onClick={() => selectUser(user)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition-colors ${idx === selectedIndex ? 'bg-slate-100 dark:bg-navy-800' : 'hover:bg-slate-50 dark:hover:bg-navy-800/50'}`}
              >
                <div className="w-8 h-8 rounded-lg bg-navy-950 dark:bg-navy-700 text-white flex items-center justify-center text-[10px] font-bold">
                  {user.name.charAt(0)}
                </div>
                <div className="flex-1 truncate">
                  <p className="text-xs font-bold text-navy-950 dark:text-white truncate">{user.name}</p>
                  <p className="text-[9px] font-medium text-slate-400 uppercase">{user.role}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MentionInput;
